'use client';

import { c as _c } from "react/compiler-runtime";
import { useUser } from '@auth0/nextjs-auth0';
import { Globe } from '@byway/icons';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { DialogTrigger, Heading, Radio, RadioGroup, Tabs } from 'react-aria-components';
import { isInternalUser } from 'utils';
import { Button } from '~/components/Button';
import { Dialog, DialogHeader, DialogScrollableContent } from '~/components/Dialog';
import { Label } from '~/components/Form/Label';
import { Tab, TabContent, TabsList } from '~/components/Tabs';
import { COUNTRY_COOKIE, LOCALE_COOKIE } from '~/constants/cookies';
import { useCurrencyContext } from '~/contexts/Currencies';
import { CONTENT_LOCALES_META } from '~/i18n/buildAlternates';
import { PREFERENCE_COUNTRIES } from '~/i18n/currency-map';
import { i18n, type Locale } from '~/i18n/i18n-config';
import { getPathname, usePathname } from '~/i18n/navigation';
import { getCookie, setSiteCookie } from '~/utils/cookies';
import { CURRENCIES, type CurrencyName, getCurrencySymbol } from '~/utils/currencyHelpers';
import styles from './PreferencesDialog.module.css';

// CLDR has no short name for some regions ("Nederland"): use the code
const localeLabel = (locale: Locale) => {
  const [language, region] = locale.split('-');
  const shortRegion = new Intl.DisplayNames([locale], {
    type: 'region',
    style: 'short'
  }).of(region) ?? region;
  const languageName = new Intl.DisplayNames([locale], {
    type: 'language'
  }).of(language);
  return `${languageName} (${shortRegion.length <= 3 ? shortRegion : region})`;
};

// No meta: not a CMS record page, exists in every locale
const isPageMissingIn = (locale: Locale) => {
  const contentLocales = globalThis.document.querySelector(`meta[name="${CONTENT_LOCALES_META}"]`)?.getAttribute('content')?.split(',');
  return contentLocales !== undefined && !contentLocales.includes(locale);
};
const resolveLocaleHref = (locale: Locale, pathname: string) => isPageMissingIn(locale) ? getPathname({
  href: '/',
  locale
}) : getPathname({
  href: pathname,
  locale
}) + globalThis.location.search;
type PreferencesContentProps = {
  selectedLocale: Locale;
  onSelectLocale: (locale: Locale) => void;
};
export const PreferencesContent = t0 => {
  const $ = _c(82);
  const {
    selectedLocale,
    onSelectLocale
  } = t0;
  const t = useTranslations();
  const currentLocale = useLocale() as Locale;
  const {
    user
  } = useUser();
  const {
    displayCurrency,
    setDisplayCurrency
  } = useCurrencyContext();
  const [country, setCountry] = useState(_temp);
  const locales = user && isInternalUser(user) ? i18n.locales : i18n.liveLocales;
  const showCountry = process.env.NEXT_PUBLIC_FF_ENABLE_COUNTRY_SELECTOR === "true";
  const showLanguage = locales.length > 1;
  let T0;
  let T1;
  let T2;
  let T3;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== country || $[1] !== currentLocale || $[2] !== displayCurrency || $[3] !== setDisplayCurrency || $[4] !== showLanguage || $[5] !== t) {
    const regionNames = new Intl.DisplayNames([currentLocale], {
      type: "region"
    });
    const currencyNames = new Intl.DisplayNames([currentLocale], {
      type: "currency"
    });
    let t13;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = value => {
        setSiteCookie(COUNTRY_COOKIE, value);
        setCountry(value);
      };
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    const changeCountry = t13;
    T3 = Tabs;
    t10 = styles.tabs;
    let t14;
    if ($[23] !== t) {
      t14 = t("aria.preferences_change");
      $[23] = t;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    if ($[25] !== t14) {
      t11 = <Heading slot="title" className="sr-only">{t14}</Heading>;
      $[25] = t14;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    let t15;
    if ($[27] !== t) {
      t15 = t("aria.preferences_change");
      $[27] = t;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    let t16;
    if ($[29] !== t) {
      t16 = showCountry && <Tab variant="minimal" id="country">{t("navigation.country")}</Tab>;
      $[29] = t;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    let t17;
    if ($[31] !== t) {
      t17 = t("navigation.currency");
      $[31] = t;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    let t18;
    if ($[33] !== t17) {
      t18 = <Tab variant="minimal" id="currency">{t17}</Tab>;
      $[33] = t17;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    let t19;
    if ($[35] !== showLanguage || $[36] !== t) {
      t19 = showLanguage && <Tab variant="minimal" id="language">{t("navigation.language")}</Tab>;
      $[35] = showLanguage;
      $[36] = t;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    if ($[38] !== t15 || $[39] !== t16 || $[40] !== t18 || $[41] !== t19) {
      t12 = <DialogHeader><TabsList variant="minimal" ariaLabel={t15}>{t16}{t18}{t19}</TabsList></DialogHeader>;
      $[38] = t15;
      $[39] = t16;
      $[40] = t18;
      $[41] = t19;
      $[42] = t12;
    } else {
      t12 = $[42];
    }
    T2 = DialogScrollableContent;
    t9 = showCountry && <TabContent id="country"><RadioGroup className={styles.group} value={country} onChange={changeCountry} data-test-id="preferences-country"><Label>{t("navigation.country_select")}</Label><div className={styles.options}>{PREFERENCE_COUNTRIES.map(code => <Radio key={code} value={code} className={styles.option}>{regionNames.of(code)}</Radio>)}</div></RadioGroup></TabContent>;
    T1 = TabContent;
    t8 = "currency";
    T0 = RadioGroup;
    t3 = styles.group;
    t4 = displayCurrency;
    if ($[43] !== setDisplayCurrency) {
      t5 = value_0 => setDisplayCurrency?.(value_0 as CurrencyName);
      $[43] = setDisplayCurrency;
      $[44] = t5;
    } else {
      t5 = $[44];
    }
    t6 = "preferences-currency";
    let t20;
    if ($[45] !== t) {
      t20 = t("navigation.currency_select");
      $[45] = t;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    if ($[47] !== t20) {
      t7 = <Label>{t20}</Label>;
      $[47] = t20;
      $[48] = t7;
    } else {
      t7 = $[48];
    }
    t1 = styles.options;
    t2 = CURRENCIES.map(code_0 => {
      const symbol = getCurrencySymbol(code_0);
      return <Radio key={code_0} value={code_0} className={styles.option}>{currencyNames.of(code_0)}<span className={styles.description}>{symbol === code_0 ? code_0 : `${code_0} – ${symbol}`}</span></Radio>;
    });
    $[0] = country;
    $[1] = currentLocale;
    $[2] = displayCurrency;
    $[3] = setDisplayCurrency;
    $[4] = showLanguage;
    $[5] = t;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = T3;
    $[10] = t1;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    T3 = $[9];
    t1 = $[10];
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t13;
  if ($[49] !== t1 || $[50] !== t2) {
    t13 = <div className={t1}>{t2}</div>;
    $[49] = t1;
    $[50] = t2;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== T0 || $[53] !== t13 || $[54] !== t3 || $[55] !== t4 || $[56] !== t5 || $[57] !== t6 || $[58] !== t7) {
    t14 = <T0 className={t3} value={t4} onChange={t5} data-test-id={t6}>{t7}{t13}</T0>;
    $[52] = T0;
    $[53] = t13;
    $[54] = t3;
    $[55] = t4;
    $[56] = t5;
    $[57] = t6;
    $[58] = t7;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== T1 || $[61] !== t14 || $[62] !== t8) {
    t15 = <T1 id={t8}>{t14}</T1>;
    $[60] = T1;
    $[61] = t14;
    $[62] = t8;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  let t16;
  if ($[64] !== currentLocale || $[65] !== locales || $[66] !== onSelectLocale || $[67] !== selectedLocale || $[68] !== showLanguage || $[69] !== t) {
    t16 = showLanguage && <TabContent id="language"><RadioGroup className={styles.group} value={selectedLocale} onChange={value_1 => onSelectLocale(value_1 as Locale)} data-test-id="preferences-language"><Label>{t("navigation.language_select")}</Label><div className={styles.options}>{locales.map(locale => <Radio key={locale} value={locale} className={styles.option}>{localeLabel(locale)}{locale !== currentLocale && isPageMissingIn(locale) && <span className={styles.description}>{t("navigation.language_homepage")}</span>}</Radio>)}</div></RadioGroup></TabContent>;
    $[64] = currentLocale;
    $[65] = locales;
    $[66] = onSelectLocale;
    $[67] = selectedLocale;
    $[68] = showLanguage;
    $[69] = t;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  let t17;
  if ($[71] !== T2 || $[72] !== t15 || $[73] !== t16 || $[74] !== t9) {
    t17 = <T2>{t9}{t15}{t16}</T2>;
    $[71] = T2;
    $[72] = t15;
    $[73] = t16;
    $[74] = t9;
    $[75] = t17;
  } else {
    t17 = $[75];
  }
  let t18;
  if ($[76] !== T3 || $[77] !== t10 || $[78] !== t11 || $[79] !== t12 || $[80] !== t17) {
    t18 = <T3 className={t10}>{t11}{t12}{t17}</T3>;
    $[76] = T3;
    $[77] = t10;
    $[78] = t11;
    $[79] = t12;
    $[80] = t17;
    $[81] = t18;
  } else {
    t18 = $[81];
  }
  return t18;
};
export const PreferencesDialog = () => {
  const $ = _c(15);
  const t = useTranslations();
  const currentLocale = useLocale() as Locale;
  const pathname = usePathname();
  const [selectedLocale, setSelectedLocale] = useState(currentLocale);
  let t0;
  if ($[0] !== currentLocale || $[1] !== pathname || $[2] !== selectedLocale) {
    t0 = isOpen => {
      if (isOpen || selectedLocale === currentLocale) {
        return;
      }
      setSiteCookie(LOCALE_COOKIE, selectedLocale);
      globalThis.location.assign(resolveLocaleHref(selectedLocale, pathname));
    };
    $[0] = currentLocale;
    $[1] = pathname;
    $[2] = selectedLocale;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const applyLocaleOnClose = t0;
  let t1;
  if ($[4] !== t) {
    t1 = t("aria.preferences_change");
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Globe decorative={true} size="large" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1) {
    t3 = <Button ariaLabel={t1} variant="icon" dataTestId="preferences-trigger" data-ua-click="edit-currency-popover">{t2}</Button>;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== selectedLocale) {
    t4 = <Dialog className={styles.dialog}><PreferencesContent selectedLocale={selectedLocale} onSelectLocale={setSelectedLocale} /></Dialog>;
    $[9] = selectedLocale;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== applyLocaleOnClose || $[12] !== t3 || $[13] !== t4) {
    t5 = <DialogTrigger onOpenChange={applyLocaleOnClose}>{t3}{t4}</DialogTrigger>;
    $[11] = applyLocaleOnClose;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
function _temp() {
  return getCookie(COUNTRY_COOKIE) ?? "GB";
}